import type {
  ExportAnnotation,
  ExportFormat,
  ExportModel,
  ExportNotesSection,
  ExportSelection,
  ExportSubjectDescriptionOptions,
  ExportTableOptions,
  Noyau,
} from '@mss_creator/shared';
import { projectInkAnnotations } from '@mss_creator/shared';
import { Button } from '@pms-digital/design-system';
import { Suspense, lazy, useLayoutEffect, useState } from 'react';
import type { ExportAnnotationDraft } from './annotation-store';
import type { ArrangeContext } from './arrange-context';
import type { ExportOptionsState } from './export-customization';
import { ExportExcelPreview } from './export-excel-preview';
import { ExportHtmlPreview } from './export-html-preview';
import type { PdfViewerDocument, PdfViewerProps } from './pdf-viewer';
import type { DisplayedPdf, ExportPreviewState } from './use-export-preview';
import type { EraseHit, ViewerTool } from './viewer-tool';
import type { RenderStatus } from './viewer-types';

/**
 * `pdfjs-dist` is ~1MB of parser plus a worker, and it is only needed once a
 * real PDF is on screen — so it is code-split rather than bundled into the
 * app's entry chunk. It also cannot be imported under jsdom (it touches
 * `DOMMatrix` at module load), which makes the split the honest shape here
 * rather than a workaround: the module genuinely has a browser-only dependency.
 */
const PdfViewer = lazy(async () => ({ default: (await import('./pdf-viewer')).PdfViewer }));

export interface ExportPreviewProps {
  format: ExportFormat;
  model: ExportModel;
  matrixTitle: string;
  companyName: string;
  matrixSujet: string | null;
  matrixContexte: string | null;
  subjectDescription: ExportSubjectDescriptionOptions;
  /** The four wire option blocks the panel drives (spec R1-R5) — passed whole so this preview and the export request cannot disagree about a setting. */
  options: ExportOptionsState;
  /** Raw (unscaled) per-column pixel widths — `matrix.viewState.columnWidths` (decision 10, 2026-08-09 doc), under the table's own overrides, as the server merges them. */
  columnWidths: Record<string, number>;
  /** Raw (unscaled) per-row pixel heights — `matrix.viewState.rowHeights`, same rationale as `columnWidths`. */
  rowHeights: Record<string, number>;
  /** The route's real `ExportSelectionState` (structurally identical to this shared schema type) — what the excel and html previews derive their label columns from. */
  selection: ExportSelection;
  /** The company's noyau — the end of the `noyau-gradient` theme's ramp. Unread by the other two themes (issue #8). */
  noyau: Noyau;
  /**
   * The PDF preview pipeline (`useExportPreview`). Owned by the route, not
   * by this component, because its `flush` backs the export button — which is
   * what makes the downloaded bytes the displayed ones (issue #11).
   */
  pdfPreview: Pick<ExportPreviewState, 'displayed' | 'status' | 'retry'>;
  /**
   * The PDF preview is the server's own render, and the backend refuses it for
   * an archived company (issue #22): say so instead of waiting on a render that
   * will not come. The excel and html previews are built here and stay.
   */
  pdfUnavailable?: boolean;
  /** Content-anchored annotations (A1-A3), all of them — baked into the export by the shared renderer. */
  annotations: readonly ExportAnnotation[];
  /** The « Notes » section the html and excel files will carry (issue #19); the PDF preview is the server's own document and needs none. */
  notes?: ExportNotesSection | null;
  /** The viewer's one exclusive pointer tool (diagnosis B5). */
  tool: ViewerTool;
  onToolChange: (tool: ViewerTool) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClearAll: () => void;
  onAddAnnotation: (annotation: ExportAnnotationDraft) => void;
  onEraseAt: (hit: EraseHit) => void;
  /** Writes the table options « Aménager » arranged (widths, column order, page breaks). */
  onTableChange: (table: ExportTableOptions) => void;
}

/**
 * Everything the PDF viewer is handed but `arrange`, which is built from the
 * pending table options (`arrangeContext`). Built in one place —
 * `pdfViewerInputs`.
 */
type PdfViewerInputs = Omit<PdfViewerProps, 'arrange'>;

/**
 * What « Aménager » edits: the *pending* table options — a drag stacks onto
 * the previous one even while the document showing it is still rendering —
 * and every column of the model in its natural order, which is how
 * `withColumnMoved` reads `columnOrder`. Null for the transposed structure:
 * its pages have no tile geometry to put handles on.
 */
function arrangeContext(
  table: ExportTableOptions,
  model: ExportModel,
  onTableChange: (table: ExportTableOptions) => void,
): ArrangeContext | null {
  if (table.structure === 'transpose') return null;
  return {
    table,
    allColumnNumbers: model.columns.map((column) => column.number),
    onTableChange,
  };
}

/**
 * The annotations the displayed document does not carry yet.
 *
 * The preview request bakes annotations into the PDF (issue #11), so the
 * overlay must paint only the rest: a new stroke shows at once, and is never
 * drawn twice once the next document lands with it baked. An erased stroke
 * that is baked stays visible, under « Mise à jour… », until that document
 * lands without it.
 */
function pendingAnnotations(
  annotations: readonly ExportAnnotation[],
  displayed: DisplayedPdf,
): ExportAnnotation[] {
  const baked = new Set(displayed.request.annotations.map((annotation) => annotation.id));
  return annotations.filter((annotation) => !baked.has(annotation.id));
}

/**
 * The displayed document as the viewer adopts it: its URL and everything its
 * overlays resolve through, all derived from its own plan — never from the
 * pending options — so the overlays sit on exactly what Chromium drew
 * (diagnosis B6: column order, logo and dates facts). The viewer keeps the
 * previous one until pdf.js shows this one, so they never jump ahead of it.
 */
function pdfViewerDocument(url: string, displayed: DisplayedPdf): PdfViewerDocument {
  const { plan } = displayed;
  return {
    url,
    geometry: {
      layouts: plan.layouts,
      pageWidthCssPx: plan.geometry.pageWidthPx,
      pageHeightCssPx: plan.geometry.pageHeightPx,
      columnPageCount: plan.columnPageCount,
    },
    lineTextsFor: (rowKey, columnNumber) =>
      (plan.model.cellContent[rowKey]?.[columnNumber] ?? []).map((line) => line.text),
    overlaysFor: (annotations) => {
      const pending = pendingAnnotations(annotations, displayed);
      // Ink is projected by the shared projector — the very one the renderer
      // uses to bake it — then shifted into page space by the layout's content
      // origin. Going through the shared function is the point: the overlay is
      // not a second opinion about where a stroke goes.
      const strokesByPage = new Map<number, { id: string; points: { x: number; y: number }[] }[]>();
      for (const annotation of pending) {
        if (annotation.kind !== 'ink') continue;
        const [projected] = projectInkAnnotations({
          annotations: [annotation],
          model: plan.model,
          tiles: plan.tiles,
          columnWidths: plan.columnWidths,
          rowHeights: plan.rowHeights,
        });
        if (projected === undefined) continue;
        const layout = plan.layouts[projected.tileIndex];
        if (layout === undefined) continue;
        const pageNumber = projected.tileIndex + 1;
        const existing = strokesByPage.get(pageNumber) ?? [];
        existing.push({
          id: annotation.id,
          points: projected.points.map((point) => ({
            x: layout.contentLeftPx + point.x,
            y: layout.contentTopPx + point.y,
          })),
        });
        strokesByPage.set(pageNumber, existing);
      }
      return { annotations: pending, strokesByPage };
    },
  };
}

function pdfViewerInputs(input: {
  url: string;
  displayed: DisplayedPdf;
  status: RenderStatus;
  onRetry: () => void;
  annotations: readonly ExportAnnotation[];
  tool: ViewerTool;
  onToolChange: (tool: ViewerTool) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClearAll: () => void;
  onAddAnnotation: (annotation: ExportAnnotationDraft) => void;
  onEraseAt: (hit: EraseHit) => void;
}): PdfViewerInputs {
  return {
    pdf: pdfViewerDocument(input.url, input.displayed),
    title: 'Aperçu PDF',
    renderStatus: input.status,
    onRetry: input.onRetry,
    tool: input.tool,
    onToolChange: input.onToolChange,
    // Every annotation, baked or pending: the viewer paints the ones the
    // document on screen lacks, and « Tout effacer » acts on all of them.
    annotations: input.annotations,
    canUndo: input.canUndo,
    canRedo: input.canRedo,
    onUndo: input.onUndo,
    onRedo: input.onRedo,
    onClearAll: input.onClearAll,
    onAddAnnotation: input.onAddAnnotation,
    onEraseAt: input.onEraseAt,
  };
}

/**
 * A blob URL for the displayed document, revoked when the document changes
 * or the preview unmounts. A resource lifecycle, not data fetching — the
 * document itself comes from `useExportPreview`'s query.
 *
 * A layout effect, so the next document's URL reaches the viewer before
 * paint, alongside its plan; the viewer then keeps both on the previous
 * document until pdf.js shows this one.
 */
function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (blob === null) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

/**
 * Read-only export preview (2026-08-16 design doc). Three genuinely
 * different rendering paths, replacing the previous single generic table
 * with format-flavored CSS:
 *  - excel: `ExportExcelPreview`, an indicative spreadsheet drawn from the
 *    shared sheet layout the backend's writer consumes (issue #12).
 *  - html: `ExportHtmlPreview`, the shared renderer's continuous document —
 *    the downloaded file itself — in an `<iframe srcDoc>` (issue #12).
 *  - pdf: the server-rendered document itself (issue #11), from the route's
 *    `useExportPreview` pipeline — the previous one stays on screen while the
 *    next renders, and the export button downloads the very bytes shown.
 */
export function ExportPreview({
  format,
  model,
  matrixTitle,
  companyName,
  matrixSujet,
  matrixContexte,
  subjectDescription,
  options,
  columnWidths,
  rowHeights,
  selection,
  noyau,
  pdfPreview,
  pdfUnavailable = false,
  annotations,
  notes = null,
  tool,
  onToolChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onClearAll,
  onAddAnnotation,
  onEraseAt,
  onTableChange,
}: ExportPreviewProps) {
  const { displayed, status, retry } = pdfPreview;
  const pdfUrl = useObjectUrl(format === 'pdf' ? (displayed?.blob ?? null) : null);

  if (format === 'html') {
    return (
      <ExportHtmlPreview
        model={model}
        selection={selection}
        options={options}
        subjectDescription={subjectDescription}
        matrixTitle={matrixTitle}
        companyName={companyName}
        matrixSujet={matrixSujet}
        matrixContexte={matrixContexte}
        noyau={noyau}
        columnWidths={columnWidths}
        rowHeights={rowHeights}
        annotations={annotations}
        notes={notes}
      />
    );
  }

  if (format === 'pdf') {
    if (pdfUnavailable) {
      return (
        <div className="flex h-full min-h-0 items-center justify-center rounded-lg border border-gray-200 bg-white px-6 text-center text-gray-500 text-small">
          L'aperçu PDF est indisponible : l'entreprise est archivée.
        </div>
      );
    }
    if (displayed === null || pdfUrl === null) {
      return (
        <div className="relative flex h-full min-h-0 flex-col">
          {status === 'failed' ? (
            <div
              role="alert"
              className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white text-small"
            >
              <p className="text-danger">Impossible de générer l'aperçu</p>
              <Button type="button" variant="primary" size="sm" onClick={retry}>
                Réessayer
              </Button>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 text-small">
              Rendu du PDF…
            </div>
          )}
        </div>
      );
    }

    const viewer = pdfViewerInputs({
      url: pdfUrl,
      displayed,
      status,
      onRetry: retry,
      annotations,
      tool,
      onToolChange,
      canUndo,
      canRedo,
      onUndo,
      onRedo,
      onClearAll,
      onAddAnnotation,
      onEraseAt,
    });

    return (
      <div className="relative flex h-full min-h-0 flex-col">
        {/* Decision S1: one surface — no « Édition » / « Aperçu PDF » tabs.
            The render status is the viewer's own badge; the old document
            stays underneath in every state (diagnosis B3). */}
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 text-small">
              Chargement de la visionneuse…
            </div>
          }
        >
          <PdfViewer {...viewer} arrange={arrangeContext(options.table, model, onTableChange)} />
        </Suspense>
      </div>
    );
  }

  // `'excel'` — the last of the three branches, kept as the function's final
  // return so the two document-rendering paths above read as early exits.
  return (
    <ExportExcelPreview
      model={model}
      selection={selection}
      options={options}
      noyau={noyau}
      columnWidths={columnWidths}
      rowHeights={rowHeights}
      notes={notes}
    />
  );
}
